import React from 'react';
import { Heart, Target, Users, MapPin, Leaf, Calendar } from 'lucide-react';
import useCounter from '../hooks/useCounter';
import NewsletterSection from '../components/NewsletterSection';

const ImpactPage = () => {
  const [lives, livesRef] = useCounter(1200);
  const [programmes, progRef] = useCounter(12);
  const [volunteers, volRef] = useCounter(85);
  const [communities, comRef] = useCounter(8);
  const [meals, mealsRef] = useCounter(5000);
  const [events, eventsRef] = useCounter(48);

  return (
    <>
      <section style={{ paddingTop: '120px', paddingBottom: '4rem', background: 'var(--off-white)' }}>
        <div className="container">
          <div className="section-header" style={{ marginBottom: '2rem' }}>
            <span className="section-tag">Our Impact</span>
            <h1>Measuring What Matters</h1>
            <p>Real numbers. Real lives. Real change in our communities.</p>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="section">
        <div className="container">
          <div className="grid grid-3" style={{ gap: '1.5rem' }}>
            {[
              { ref: livesRef, num: lives, suffix: '+', label: 'Lives Impacted', icon: <Heart size={36} />, color: 'var(--rose)' },
              { ref: progRef, num: programmes, suffix: '+', label: 'Active Programmes', icon: <Target size={36} />, color: 'var(--teal)' },
              { ref: volRef, num: volunteers, suffix: '+', label: 'Active Volunteers', icon: <Users size={36} />, color: 'var(--navy)' },
              { ref: comRef, num: communities, suffix: '', label: 'Communities Served', icon: <MapPin size={36} />, color: 'var(--gold)' },
              { ref: mealsRef, num: meals, suffix: '+', label: 'Meals Provided', icon: <Leaf size={36} />, color: 'var(--teal)' },
              { ref: eventsRef, num: events, suffix: '', label: 'Events Per Year', icon: <Calendar size={36} />, color: 'var(--rose)' },
            ].map((stat, i) => (
              <div key={i} ref={stat.ref} className="scroll-animate card" style={{
                padding: '2rem', textAlign: 'center', transitionDelay: `${i * 100}ms`
              }}>
                <div style={{
                  width: '80px', height: '80px', borderRadius: '16px',
                  background: `${stat.color}12`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', margin: '0 auto 1rem', color: stat.color
                }}>
                  {stat.icon}
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '2.5rem', fontWeight: 800, color: 'var(--navy)' }}>
                  {stat.num.toLocaleString()}{stat.suffix}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section style={{ background: 'var(--off-white)', padding: '5rem 0' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Impact Stories</span>
            <h2>Lives Transformed</h2>
          </div>
          <div className="grid grid-2" style={{ gap: '2rem' }}>
            {[
              { title: 'From Learner to Leader', story: 'Sipho joined our after-school programme struggling with basic numeracy. Two years later, he graduated top of his class and received a bursary to study engineering. Today he mentors other learners in our programme.', tag: 'Education' },
              { title: 'Community Garden Success', story: 'What started as a small patch of land in Diepsloot is now a thriving community garden feeding over 40 families. The project has also created 6 part-time jobs and trained 30 community members in sustainable farming.', tag: 'Food Security' },
              { title: 'Youth Business Incubator', story: 'Through our entrepreneurship bootcamp, 35 young people have launched small businesses, from catering services to tech solutions. Combined, these businesses now employ over 50 community members.', tag: 'Youth Development' },
              { title: 'Mental Health Breakthrough', story: 'Our wellness workshops have reached over 800 community members, breaking the stigma around mental health. Three support groups now run independently, led by trained community facilitators.', tag: 'Wellness' }
            ].map((story, i) => (
              <div key={i} className="scroll-animate card" style={{ padding: '2rem', transitionDelay: `${i * 150}ms` }}>
                <span className="programme-card-category cat-education" style={{ marginBottom: '0.75rem', display: 'inline-block' }}>{story.tag}</span>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>{story.title}</h3>
                <p style={{ fontSize: '0.9rem' }}>{story.story}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NewsletterSection />
    </>
  );
};

export default ImpactPage;
